import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getUser, clearSession, ROLE_NAMES, api, fmt, setRates } from './api';
import { Modal, Field, Input, useToast } from './ui';
import { Icons } from './icons';
import { LogoMark } from './Logo';
import { ErrorBoundary } from './ErrorBoundary';

interface NavDef { to: string; icon: string; label: string; roles?: string[]; badge?: number }

const MAIN_ROLES = ['admin', 'zakaz_operator', 'rastamojka', 'sotuv', 'rahbar'];

const TITLES: Record<string, string> = {
  '/': 'Bosh sahifa', '/orders': 'Zakazlar', '/receipts': 'Kirim (priyomka)',
  '/customs': 'Rastamojka', '/shortages': 'Yetmovchiliklar', '/products': 'Katalog',
  '/prices': 'Narxlar', '/reports': 'Hisobotlar', '/customs-groups': 'Rastamojka guruhlari',
  '/categories': 'Kategoriyalar', '/suppliers': 'Zavodlar', '/currencies': 'Valyutalar',
  '/price-history': 'Narxlar dinamikasi', '/prixod-matrix': 'Prixod tarixi',
  '/contract': 'Shartnoma bera olamiz', '/users': 'Foydalanuvchilar',
  '/payments': "To'lovlar va akt sverka", '/audit': 'Audit jurnali',
};

export function Layout() {
  const user = getUser();
  const nav = useNavigate();
  const loc = useLocation();
  const toast = useToast();

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  const [pwd, setPwd] = useState<{ oldPassword: string; newPassword: string } | null>(null);
  const [installEvt, setInstallEvt] = useState<any>(null);
  const [shortCount, setShortCount] = useState(0);
  const [usdRate, setUsdRate] = useState<number | null>(null);
  const [cnyRate, setCnyRate] = useState<number | null>(null);

  const [theme, setTheme] = useState(() => localStorage.getItem('alight_theme') || 'dark');
  const toggleTheme = () => {
    const t = theme === 'dark' ? 'light' : 'dark';
    setTheme(t); localStorage.setItem('alight_theme', t);
    document.documentElement.setAttribute('data-theme', t);
  };

  useEffect(() => {
    const h = (e: any) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);
  useEffect(() => {
    if (MAIN_ROLES.includes(user?.role)) {
      api.get('/dashboard').then((d) => setShortCount(d.openShortages || 0)).catch(() => {});
    }
    // kurs chipi hammaga kerak — narx kiritadiganlarga ham (avval faqat 3 rol ko'rardi)
    if (user?.role) {
      api.get('/currencies').then((cs) => {
        setRates(cs);   // butun ilova bo'ylab ¥/so'm → $ hisobi uchun
        const uzs = cs.find((c: any) => c.code === 'UZS');
        const cny = cs.find((c: any) => c.code === 'CNY');
        if (uzs) setUsdRate(Number(uzs.rateToBase));
        if (cny) setCnyRate(Number(cny.rateToBase));
      }).catch(() => {});
    }
  }, []);

  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };
  const logout = () => { clearSession(); nav('/login'); };
  const changePwd = async () => {
    try {
      await api.post('/auth/change-password', pwd);
      // Parol o'zgargach eski tokenlar bekor bo'ladi — qaytadan kirish kerak
      toast("Parol o'zgartirildi — qaytadan kiring");
      setPwd(null);
      setTimeout(() => { clearSession(); nav('/login'); }, 900);
    } catch (e: any) { toast(e.message, true); }
  };

  /** O'g'irlangan yoki unutilgan qurilmadagi sessiyalarni bekor qiladi. */
  const logoutAll = async () => {
    if (!confirm("Barcha qurilmalardagi sessiyalar bekor qilinadi. Siz ham qaytadan kirasiz. Davom etamizmi?")) return;
    try {
      await api.post('/auth/logout-all', {});
      clearSession(); nav('/login');
    } catch (e: any) { toast(e.message, true); }
  };

  const PRIMARY: NavDef[] = [
    { to: '/', icon: 'home', label: 'Bosh sahifa', roles: MAIN_ROLES },
    { to: '/orders', icon: 'orders', label: 'Zakazlar', roles: ['admin', 'zakaz_operator', 'rahbar'] },
    { to: '/receipts', icon: 'receipt', label: 'Kirim', roles: ['admin', 'zakaz_operator', 'rastamojka'] },
    { to: '/customs', icon: 'customs', label: 'Rastamojka', roles: ['admin', 'rastamojka', 'kargo', 'rahbar', 'zakaz_operator'] },
    { to: '/shortages', icon: 'warning', label: 'Yetmovchilik', roles: ['admin', 'zakaz_operator', 'rastamojka', 'rahbar'], badge: shortCount },
    { to: '/products', icon: 'catalog', label: 'Katalog', roles: MAIN_ROLES },
    { to: '/prices', icon: 'money', label: 'Narxlar', roles: ['admin', 'sotuv', 'rahbar'] },
    { to: '/reports', icon: 'chart', label: 'Hisobotlar', roles: ['admin', 'zakaz_operator', 'rastamojka', 'sotuv', 'rahbar'] },
    { to: '/contract', icon: 'receipt', label: 'Shartnoma', roles: ['admin', 'sotuv', 'rahbar'] },
    { to: '/payments', icon: 'currency', label: "To'lovlar", roles: ['admin', 'zakaz_operator', 'rahbar'] },
    // Buxgalter FAQAT shu bo'limni ko'radi -- qolgan hamma yo'l uning rolisiz.
    { to: '/accounting', icon: 'money', label: 'Buxgalteriya', roles: ['admin', 'buxgalter', 'rahbar'] },
  ];
  const SPRAV: NavDef[] = [
    { to: '/customs-groups', icon: 'group', label: 'Rastamojka guruhlari', roles: ['admin', 'rastamojka', 'rahbar'] },
    { to: '/categories', icon: 'tree', label: 'Kategoriyalar', roles: ['admin', 'zakaz_operator', 'rastamojka'] },
    { to: '/suppliers', icon: 'supplier', label: 'Zavodlar', roles: MAIN_ROLES },
    { to: '/currencies', icon: 'currency', label: 'Valyutalar', roles: ['admin', 'rahbar', 'sotuv'] },
    { to: '/price-history', icon: 'history', label: 'Narxlar dinamikasi', roles: ['admin', 'rastamojka', 'sotuv', 'rahbar'] },
    { to: '/prixod-matrix', icon: 'chart', label: 'Prixod tarixi', roles: ['admin', 'rastamojka', 'sotuv', 'rahbar'] },
    { to: '/users', icon: 'users', label: 'Foydalanuvchilar', roles: ['admin'] },
    { to: '/audit', icon: 'history', label: 'Audit jurnali', roles: ['admin', 'rahbar'] },
  ];
  const primary = PRIMARY.filter((n) => !n.roles || n.roles.includes(user?.role));
  const sprav = SPRAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  const NavItem = ({ n }: { n: NavDef }) => {
    const Ico = Icons[n.icon] || Icons.home;
    return (
      <NavLink to={n.to} end={n.to === '/'} onClick={() => setMobOpen(false)}
        className={({ isActive }) => 'snav-item' + (isActive ? ' active' : '')}>
        <Ico size={18} />
        <span className="snav-label">{n.label}</span>
        {!!n.badge && <span className="snav-badge">{n.badge}</span>}
      </NavLink>
    );
  };

  const pageTitle = TITLES[loc.pathname]
    || (loc.pathname.startsWith('/orders/') ? 'Zakaz' : '')
    || (loc.pathname.startsWith('/customs/') ? 'Rastamojka' : '')
    || (loc.pathname.startsWith('/products/') ? 'Mahsulot' : '')
    || 'Alight';

  return (
    <div className="shell">
      {mobOpen && <div className="mnav-backdrop" onClick={() => setMobOpen(false)} />}
      <aside className={'sidebar' + (mobOpen ? ' open' : '')}>
        <div className="brand">
          <LogoMark size={35} />
          <div>
            <div className="brand-name">ALIGHT</div>
            <div className="brand-sub">Import Manager</div>
          </div>
        </div>

        {primary.map((n) => <NavItem key={n.to} n={n} />)}

        {sprav.length > 0 && <div className="nav-group">Spravochniklar</div>}
        {sprav.map((n) => <NavItem key={n.to} n={n} />)}

        <div className="side-user" onClick={() => setMenuOpen(!menuOpen)}>
          <div className="avatar">{(user?.fullName || 'U')[0].toUpperCase()}</div>
          <div style={{ minWidth: 0 }}>
            <div className="u-name">{user?.fullName}</div>
            <div className="u-role">{ROLE_NAMES[user?.role] || user?.role}</div>
          </div>
          {menuOpen && (
            <div className="user-menu" onClick={(e) => e.stopPropagation()}>
              <button className="menu-item" onClick={() => { setMenuOpen(false); setPwd({ oldPassword: '', newPassword: '' }); }}>
                <Icons.key size={16} /> Parolni o'zgartirish
              </button>
              <button className="menu-item" onClick={() => { setMenuOpen(false); logoutAll(); }}>
                <Icons.shield size={16} /> Barcha qurilmalardan chiqish
              </button>
              {installEvt && (
                <button className="menu-item" onClick={() => { setMenuOpen(false); install(); }}>
                  <Icons.download size={16} /> Ilovani o'rnatish
                </button>
              )}
              <button className="menu-item danger" onClick={logout}>
                <Icons.logout size={16} /> Chiqish
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="main-col">
        <header className="topbar">
          <button className="icon-btn nav-burger" onClick={() => setMobOpen(true)} aria-label="Menyu">
            <Icons.menu size={21} />
          </button>
          <span className="topbar-title">{pageTitle}</span>
          <div className="spacer" />
          {(usdRate != null || cnyRate != null) && (
            <span className="rate-chip" title="1 USD kursi (Valyutalar bo'limida yangilanadi)">
              <Icons.currency size={15} />
              {cnyRate != null && <>¥ {fmt(cnyRate, 2)}</>}
              {cnyRate != null && usdRate != null && <span style={{ opacity: .5 }}>·</span>}
              {usdRate != null && <>{fmt(usdRate)} so'm</>}
            </span>
          )}
          {installEvt && <button className="btn btn-sm btn-primary" onClick={install}>⬇ O'rnatish</button>}
          <button className="icon-btn" onClick={toggleTheme} title={theme === 'dark' ? "Yorug' rejim" : 'Tungi rejim'}>
            {theme === 'dark' ? <Icons.sun size={18} /> : <Icons.moon size={18} />}
          </button>
        </header>

        <main className="page">
          {/* Bir bo'limdagi xato butun ilovani qulflab qo'ymasin: menyu joyida
              qoladi, boshqa sahifaga o'tilsa xato o'zi tozalanadi. */}
          <ErrorBoundary resetKey={loc.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {pwd && (
        <Modal title="Parolni o'zgartirish" onClose={() => setPwd(null)}>
          <Field label="Joriy parol"><Input type="password" value={pwd.oldPassword} onChange={(e) => setPwd({ ...pwd, oldPassword: e.target.value })} /></Field>
          <Field label="Yangi parol"><Input type="password" value={pwd.newPassword} onChange={(e) => setPwd({ ...pwd, newPassword: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn" onClick={() => setPwd(null)}>Bekor</button>
            <button className="btn btn-primary" onClick={changePwd}>Saqlash</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
